import { useState, useEffect } from 'react';
import { Box, CircularProgress, makeStyles } from '@material-ui/core';

// Helpers
import { blueBar } from '../../helpers/common';
import {
    OPTIONS_DEVICES,
    SMART_DATA,
    HOME_DATA,
    mergeDataAlarmSmart,
    mergeDataAlarmHome,
    mergeMonoPhaseAlarmSmartNewVersion,
    mergeTrifasicoPhaseAlarmSmartNewVersion,
    transformAlarmConfig
} from './helpers';

// Componentes
import ButtonComponent from "./ButtonComponent";
import TableInputsComponent from './TableInputsComponent';

// Services
import { putControlSetting } from '../../services/meters';
import { connectWebSocket } from '../../helpers/socketFunctions';

const useStyles = makeStyles(theme => ({
    root: {
        border: "0.5px solid #B6B1B1",
        borderRadius: "4px",
        padding: theme.spacing(1),
        width: "100%",
    },
    configForAlarmsOnDevices: {
        border: "0.5px solid #B6B1B1",
        borderRadius: "4px",
        padding: theme.spacing(1),
        width: "100%",
        heigth: "100vh",
        display: "grid"
    },
    title: {
        fontSize: theme.spacing(1.9),
        fontWeight: '600',
        color: theme.palette.primary.main
    },
    subTitle: {
        color: theme.palette.grey.hairline,
        fontWeight: '400'
    },
    titleSecondary: {
        fontSize: theme.spacing(1.6),
        fontWeight: '600',
        color: theme.palette.primary.main,
        backgroundColor: "#94949436",
        padding: theme.spacing(1),
        borderRadius: "4px"
    },
}));

export default function AlarmsControl({
    setLoading,
    setAlertState,
    options,
    stateData,
    loading,
    selectedNode,
    setLoadingInbox,
    setStateHelper,
    setSatateData
}) {
    const classes = useStyles();
    const blueLine = blueBar();

    // Estado de la tabla de tension.
    const [stateDataVoltage, setStateDataVoltage] = useState({});
    // Estado de la tabla de corriente.
    const [stateDataCurrent, setStateDataCurrent] = useState({});
    // Estado de la tabla de potencia.
    const [stateDataPower, setStateDataPower] = useState({});
    // ✅ Potencia adquirida maxima (1 solo campo)
    const [stateDataAcquired, setStateDataAcquired] = useState({ dmax: '' });

    const [saving] = useState(false);

    // Valida si es monofasico o trifasico
    function getPhaseType(phases) {
        const activePhases = Object.values(phases || {}).filter(Boolean).length;
        if (activePhases === 3) return 'TRIFASICO';
        if (activePhases === 1) return 'MONOFASICO';
        if (activePhases === 2) return 'MONOFASICO';
        return 'INVALIDO';
    }

    const isMono = getPhaseType(selectedNode?.info_fases) === 'MONOFASICO';

    useEffect(() => {
        // OLD VERSION: SMART/HOME
        if (options.plan === OPTIONS_DEVICES.SMART) {
            if (stateData?.UMBRALES_GET?.MSJ?.rta) {
                const mergeObj = mergeDataAlarmSmart(stateData?.UMBRALES_GET?.MSJ?.rta);
                setStateDataVoltage(mergeObj.VOLTAGE);
                setStateDataCurrent(mergeObj.CURRENT);
                setStateDataPower(mergeObj.POWER);
                setStateDataAcquired(mergeObj.ACQUIRED ?? SMART_DATA.ACQUIRED);
            } else {
                setStateDataVoltage(SMART_DATA.VOLTAGE);
                setStateDataCurrent(SMART_DATA.CURRENT);
                setStateDataPower(SMART_DATA.POWER);
                setStateDataAcquired(SMART_DATA.ACQUIRED);
            }
        }
        else if (options.plan === OPTIONS_DEVICES.HOME) {
            if (stateData?.UMBRALES_GET?.MSJ?.rta) {
                const mergeObj = mergeDataAlarmHome(stateData?.UMBRALES_GET?.MSJ?.rta);
                setStateDataVoltage(mergeObj.VOLTAGE);
                setStateDataCurrent(mergeObj.CURRENT);
                setStateDataPower(mergeObj.POWER);
                setStateDataAcquired(mergeObj.ACQUIRED ?? HOME_DATA.ACQUIRED);
            } else {
                setStateDataVoltage(HOME_DATA.VOLTAGE);
                setStateDataCurrent(HOME_DATA.CURRENT);
                setStateDataPower(HOME_DATA.POWER);
                setStateDataAcquired(HOME_DATA.ACQUIRED);
            }
        }
        else {
            // NEW VERSION:
            // stateData ya viene mergeado afuera como {VOLTAGE, CURRENT, POWER, ACQUIRED}
            setStateDataVoltage(stateData?.VOLTAGE ?? {});
            setStateDataCurrent(stateData?.CURRENT ?? {});
            setStateDataPower(stateData?.POWER ?? {});
            setStateDataAcquired(stateData?.ACQUIRED ?? { dmax: '' });
        }
    }, [stateData, saving, options.plan]);

    //Funcion q saca el guion del final del string
    function removeLastDashNumber(strAux) {
        let str = strAux;
        if (!str) {
            str = selectedNode?.realtime?.llave;
        }
        if (str?.includes("-")) {
            return str.substring(0, str.lastIndexOf("-"));
        }
        return str;
    }

    function getLastPartAfterDash(str) {
        let s = str;
        if (!s) {
            s = selectedNode?.realtime?.llave;
        }
        if (typeof s === 'string' && s.includes("-")) {
            const lastPart = s.split("-").pop();
            return lastPart !== undefined ? Number(lastPart) : s;
        }
        return s;
    }

    //Onchange de la tabla tension.
    const handleChangeVoltage = (e, name) => {
        setStateDataVoltage({
            ...stateDataVoltage,
            [name]: {
                ...stateDataVoltage[name],
                [e.target.name]: e.target.value
            }
        });
    };

    //Onchange de la tabla corriente.
    const handleChangeCurrent = (e, name) => {
        setStateDataCurrent({
            ...stateDataCurrent,
            [name]: {
                ...stateDataCurrent[name],
                [e.target.name]: e.target.value
            }
        });
    };

    //Onchange de la tabla potencia.
    const handleChangePower = (e, name) => {
        setStateDataPower({
            ...stateDataPower,
            [name]: {
                ...stateDataPower[name],
                [e.target.name]: e.target.value
            }
        });
    };

    // ✅ adquirida: 1 solo campo (dmax)
    const handleChangeAcquired = (e) => {
        setStateDataAcquired(prev => ({
            ...prev,
            dmax: e.target.value
        }));
    };

    //Transforma un objeto en array.
    const arryMergeObj = (objetoOriginal) => {
        const arrayObjetos = [];
        for (const clave in (objetoOriginal || {})) {
            if (Object.prototype.hasOwnProperty.call(objetoOriginal, clave)) {
                arrayObjetos.push({
                    name: clave,
                    values: objetoOriginal[clave],
                });
            }
        }
        return arrayObjetos;
    };

    //Funcion q realiza el merge de la data del componente a la q necesita el protocolo mqtt. (OLD VERSION)
    const mergeData = () => {
        const params = [];
        const properties = {
            voltage: {
                R: { name: "r_v", max: "", min: "" },
                S: { name: "s_v", max: "", min: "" },
                T: { name: "t_v", max: "", min: "" }
            },
            current: {
                R: { name: "r_i", max: "", min: "" },
                S: { name: "s_i", max: "", min: "" },
                T: { name: "t_i", max: "", min: "" }
            },
            power: {
                R: { name: "r_p", max: "", min: "" },
                S: { name: "s_p", max: "", min: "" },
                T: { name: "t_p", max: "", min: "" },
                Σ: { name: "tot_p", max: "", min: "" }
            }
        };

        const stateDataArr = [
            [stateDataVoltage, properties.voltage],
            [stateDataCurrent, properties.current],
            [stateDataPower, properties.power]
        ];

        stateDataArr.forEach(([data, props]) => {
            Object.entries(data || {}).forEach(([property, value]) => {
                if (props.hasOwnProperty(property)) {
                    const prop = props[property];
                    prop.max = value.max;
                    prop.min = value.min;
                    params.push({ name: prop.name + "max", value: prop.max });
                    params.push({ name: prop.name + "min", value: prop.min });
                }
            });
        });

        return params;
    };

    const HandleSubmit = async () => {
        setLoadingInbox(true);

        let msg = "Ocurrio un error inesperado";
        let type = "error";

        const bodyAux = {
            key: "SET_ALARM",
            params: mergeData()
        };

        try {
            // OLD VERSION
            if (selectedNode?.producto === 'SMART' || selectedNode?.producto === 'HOME') {
                await putControlSetting(options?.id, bodyAux);
                type = "success";
                msg = "Su configuración se guardó correctamente.";

                setSatateData({
                    ...stateData,
                    UMBRALES_GET: {
                        key: "UMBRALES_GET",
                        MSJ: { rta: mergeData() }
                    }
                });
            }
            else {
                // NEW VERSION
                const idAux = getLastPartAfterDash(selectedNode?.realtime?.llave);

                const objConfig = {
                    id: idAux,
                    // tension (v)
                    v: stateDataVoltage,
                    // corriente (i)
                    i: stateDataCurrent,
                    // potencia (p) (incluye Σ dentro de stateDataPower)
                    p: stateDataPower,
                    // ✅ adquirida (1 solo campo)
                    acquired: stateDataAcquired
                };

                const message = {
                    value: {
                        mac: removeLastDashNumber(selectedNode?.realtime?.llave),
                        time: null,
                        device_type: selectedNode?.realtime?.producto,
                        action: "SET_ALARMS",
                        extra_info: {
                            id: idAux,
                            type_of_meter: getPhaseType(selectedNode?.info_fases),
                            alarms: [transformAlarmConfig(objConfig, idAux)]
                        }
                    },
                    type: "ALARM_MANAGER",
                };
                connectWebSocket(message, setStateHelper);
                return;
            }
        }
        catch (error) {
            if (error?.response?.data) {
                msg = error?.response?.data;
            }
        }
        setAlertState({
            close: true,
            msg: msg,
            type: type
        });
        setLoadingInbox(false);
    };

    const classRoot = () => ({
        border: "0.5px solid #B6B1B1",
        borderRadius: "4px",
        width: "100%",
    });

    const validatePotenciaAdquiridaField = () => {
        // Si es monofasico, no mostramos el campo
        if (selectedNode?.producto === 'SMART' || selectedNode?.producto === 'HOME') {
            // En SMART/HOME old, solo mostramos si viene en la data    
            return false;
        }
        if (selectedNode?.producto === 'pro5' && selectedNode?.producto === 'pro15') {
            return false;
        }
        // En NEW VERSION, siempre mostramos si no es monofasico    
        return true;
    }


    return (
        <>
            {!loading && (
                <Box style={classRoot()}>

                    {/* titulo */}
                    <Box mt={1} ml={1} className={classes.title}>
                        {"Alarma"}
                        <span className={classes.subTitle}>
                            {` | ${"umbrales"}`}
                        </span>
                    </Box>

                    {blueLine}

                    {/* Tension */}
                    <Box>
                        <Box ml={1} className={classes.titleSecondary}>
                            {"Tension"}
                            <span className={classes.subTitle}>
                                {` | ${"por fase (V)"}`}
                            </span>
                        </Box>

                        {arryMergeObj(stateDataVoltage)?.map((element, indx) => (
                            <TableInputsComponent
                                stateDataVoltage={stateDataVoltage}
                                key={indx}
                                handleChange={handleChangeVoltage}
                                element={element}
                            />
                        ))}
                    </Box>

                    {blueLine}

                    {/* Corriente */}
                    <Box>
                        <Box ml={1} className={classes.titleSecondary}>
                            {"Corriente"}
                            <span className={classes.subTitle}>
                                {` | ${"por fase (A)"}`}
                            </span>
                        </Box>

                        {arryMergeObj(stateDataCurrent)?.map((element, indx) => (
                            <TableInputsComponent
                                key={indx}
                                handleChange={handleChangeCurrent}
                                element={element}
                            />
                        ))}
                    </Box>

                    {blueLine}

                    {/* Potencia */}
                    <Box>
                        <Box ml={1} className={classes.titleSecondary}>
                            {"Potencia"}
                            <span className={classes.subTitle}>
                                {` | ${"por fase (W)"}`}
                            </span>
                        </Box>

                        {arryMergeObj(stateDataPower)?.map((element, indx) => (
                            <TableInputsComponent
                                key={indx}
                                handleChange={handleChangePower}
                                element={element}
                            />
                        ))}
                    </Box>


                    {/* ✅ Potencia adquirida (solo si NO es monofasico) */}
                    {validatePotenciaAdquiridaField() && (
                        <>
                            {blueLine}

                            <Box>
                                <Box ml={1} className={classes.titleSecondary}>
                                    {"Potencia adquirida"}
                                    <span className={classes.subTitle}>
                                        {` | ${"máxima (W)"}`}
                                    </span>
                                </Box>

                                {/* 1 campo */}
                                <Box px={2} py={1}>
                                    <TableInputsComponent
                                        handleChange={(e) => handleChangeAcquired(e)}
                                        element={{
                                            name: 'dmax',
                                            values: { max: stateDataAcquired?.dmax ?? '' } // usamos "max" para reutilizar el componente
                                        }}
                                    />
                                </Box>
                            </Box>
                        </>
                    )}

                    {blueLine}

                    <Box display='flex' justifyContent='center' flexDirection={"column"} alignItems={"center"}>
                        {!saving && (
                            <ButtonComponent
                                children={
                                    <Box ml={1} style={{ fontWeight: "bold" }}>{"Enviar"}</Box>
                                }
                                HandleSubmit={HandleSubmit}
                                backgroundColorButton={"primary"}
                            />
                        )}

                        {saving && (
                            <Box display="flex" justifyContent="center" mt={2}>
                                <CircularProgress />
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </>
    );
}
